"use client";

import { CSSProperties } from "react";

import { TextBlock } from "@/lib/utils/lomdaV3/types";
import { t } from "@/lib/localization";
import { useLomdaV3Editor } from "../../../store/useLomdaV3Editor";
import { resolveTableAppearance, tableModel } from "../../../lib/blockRegistry";
import RichText from "./richText";

/**
 * Data table = a real grid of independent rich-text cells. Each cell is its own
 * editor, so focusing a cell shows the text toolbar (no table chrome); the
 * table's own settings (rows/columns/header) live on the block settings bar.
 */
export default function TableBlock({
    block,
    editable,
}: {
    block: TextBlock;
    editable: boolean;
}) {
    const updateBlock = useLomdaV3Editor((s) => s.updateBlock);
    const theme = useLomdaV3Editor((s) => s.metadata.theme);
    const data = block.data;
    if (data.variant !== "table") return null;

    const { rows, headerRow } = tableModel(data);
    const a = resolveTableAppearance(data.appearance, theme);
    const tableStyle = {
        "--lomda-table-bg": a.cardBackground,
        "--lomda-table-border": a.border,
        "--lomda-table-bw": `${a.borderWidth}px`,
        "--lomda-table-header-bg": a.headerBackground,
    } as CSSProperties;

    const setCell = (r: number, c: number, html: string) =>
        updateBlock(block.id, {
            rows: rows.map((row, ri) =>
                row.map((cell, ci) => (ri === r && ci === c ? html : cell))
            ),
            headerRow,
            html: undefined,
        });

    return (
        <div className="overflow-x-auto">
            <table className="lomda-v3-table" style={tableStyle}>
                <tbody>
                    {rows.map((row, ri) => (
                        <tr key={ri}>
                            {row.map((cell, ci) => {
                                const isHeader = headerRow && ri === 0;
                                const Cell = isHeader ? "th" : "td";
                                return (
                                    <Cell key={ci}>
                                        <RichText
                                            initialHtml={cell}
                                            placeholder={
                                                isHeader
                                                    ? t(
                                                          "lomda-v3-editor.text.column-n",
                                                          undefined,
                                                          { number: ci + 1 }
                                                      )
                                                    : undefined
                                            }
                                            editable={editable}
                                            useGlobalToolbar
                                            blockId={block.id}
                                            // Header cells and body cells are two
                                            // groups: changing one cell's tag
                                            // applies to the rest of its group.
                                            groupKey={`${block.id}:${
                                                isHeader
                                                    ? "table-header"
                                                    : "table-body"
                                            }`}
                                            onCommit={(html) =>
                                                setCell(ri, ci, html)
                                            }
                                        />
                                    </Cell>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
